/**
 * RBAC-8.2 · What changed recently, filterable (FR-29, FR-33).
 *
 * The log is `role_audit_events`, written by triggers (`20261103a5`) from every route: the editor,
 * assign, copy, remove access, hand over, duplicate, adopt, the agency sync and the SQL editor. This
 * module knows what the rows MEAN: their sentence, the four filters and the RPC arguments they become.
 * It holds no rule about who may read; that is `role_audit_log()`'s (roles.administer, else 42501).
 *
 * ⚠️ FOUR FILTERS, AS SLACK'S AUDIT LOG HAS THEM: date range, acting user, who or what was affected,
 * and kind of change. A role can have hundreds of holders, so an unfiltered list cannot be the only view.
 * ⚠️ ADMINISTRATION IS ITS OWN KIND. A change that gives or takes `roles.administer` is written as an
 * extra `administration_granted` / `_removed` event, and "Administration" is a kind you can pick, not
 * something you find by reading every row.
 *
 * Pure, no DOM, no Supabase.
 */

import { listOf, permissionTitle } from './rolePermissionDiff';

export const ROLE_AUDIT_LOG_RPC = 'role_audit_log';
export const ROLE_AUDIT_FACETS_RPC = 'role_audit_log_facets';

export const ROLE_AUDIT_EVENT_TYPES = [
  'role_created',
  'role_renamed',
  'role_deleted',
  'permissions_changed',
  'assignment_added',
  'assignment_removed',
  'assignment_changed',
  'access_removed',
  'access_restored',
  'administration_granted',
  'administration_removed',
] as const;
export type RoleAuditEventType = (typeof ROLE_AUDIT_EVENT_TYPES)[number];

/** The kinds the filter offers, each a set of event types. Administration first: FR-33. */
export const ROLE_AUDIT_KINDS = [
  { id: 'administration', label: 'Administration given or taken away', types: ['administration_granted', 'administration_removed'] },
  { id: 'assignments', label: 'Someone given or moved off a role', types: ['assignment_added', 'assignment_removed', 'assignment_changed'] },
  { id: 'permissions', label: 'What a role can do', types: ['permissions_changed'] },
  { id: 'roles', label: 'Roles created, renamed or deleted', types: ['role_created', 'role_renamed', 'role_deleted'] },
  { id: 'access', label: 'Access removed or restored', types: ['access_removed', 'access_restored'] },
] as const satisfies readonly { id: string; label: string; types: readonly RoleAuditEventType[] }[];
export type RoleAuditKind = (typeof ROLE_AUDIT_KINDS)[number]['id'];

export interface RoleAuditEvent {
  id: number;
  occurredAt: string;
  type: RoleAuditEventType;
  /** A Joules role's own change: seen by every account, actor shown as Joules. */
  joules: boolean;
  actorId: string | null;
  /** NULL when nobody was signed in: the SQL editor, a scheduled job, the service role. */
  actorLabel: string | null;
  roleId: number | null;
  roleName: string | null;
  fromRoleId: number | null;
  fromRoleName: string | null;
  subjectId: string | null;
  subjectLabel: string | null;
  detail: Record<string, unknown>;
}

export interface RoleAuditPage {
  events: RoleAuditEvent[];
  /** Pass back as `before` for the next page; null on the last. */
  nextBeforeId: number | null;
}

export interface FacetOption<Id> {
  id: Id;
  label: string;
  count: number;
}

export interface RoleAuditFacets {
  total: number;
  oldest: string | null;
  actors: FacetOption<string>[];
  people: FacetOption<string>[];
  roles: FacetOption<number>[];
  types: Partial<Record<RoleAuditEventType, number>>;
}

/** "Who or what was affected" is one picker over people and roles. */
export type AffectedFilter = { kind: 'person'; id: string } | { kind: 'role'; id: number };

export interface RoleAuditFilters {
  /** Inclusive calendar days, `YYYY-MM-DD`, in the viewer's time zone. */
  fromDay: string | null;
  toDay: string | null;
  actorId: string | null;
  affected: AffectedFilter | null;
  kind: RoleAuditKind | null;
}

export const NO_FILTERS: RoleAuditFilters = { fromDay: null, toDay: null, actorId: null, affected: null, kind: null };

export const PAGE_SIZE = 50;

// ── Parsing ───────────────────────────────────────────────────────────────────

const isId = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0;
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isDate = (v: unknown): v is string => typeof v === 'string' && !Number.isNaN(Date.parse(v));
const strOrNull = (v: unknown): v is string | null => v === null || typeof v === 'string';
const idOrNull = (v: unknown): v is number | null => v === null || isId(v);

function parseEvent(raw: unknown): RoleAuditEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isId(r.id) || !isDate(r.occurred_at)) return null;
  if (!ROLE_AUDIT_EVENT_TYPES.includes(r.event_type as RoleAuditEventType)) return null;
  if (typeof r.joules !== 'boolean') return null;
  if (!strOrNull(r.actor_id) || !strOrNull(r.actor_label) || !strOrNull(r.role_name) || !strOrNull(r.from_role_name)) return null;
  if (!strOrNull(r.subject_user_id) || !strOrNull(r.subject_label)) return null;
  if (!idOrNull(r.role_id) || !idOrNull(r.from_role_id)) return null;
  if (r.role_id === null && r.subject_user_id === null) return null;
  const detail = r.detail && typeof r.detail === 'object' && !Array.isArray(r.detail) ? (r.detail as Record<string, unknown>) : {};
  return {
    id: r.id,
    occurredAt: r.occurred_at,
    type: r.event_type as RoleAuditEventType,
    joules: r.joules,
    actorId: r.actor_id,
    actorLabel: r.actor_label,
    roleId: r.role_id,
    roleName: r.role_name,
    fromRoleId: r.from_role_id,
    fromRoleName: r.from_role_name,
    subjectId: r.subject_user_id,
    subjectLabel: r.subject_label,
    detail,
  };
}

/** The whole page, or null if any row is unreadable: a log that silently drops rows is worse than none. */
export function parseRoleAuditPage(raw: unknown): RoleAuditPage | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.events) || !idOrNull(r.next_before_id ?? null)) return null;
  const events = r.events.map(parseEvent);
  if (events.some((e) => e === null)) return null;
  return { events: events as RoleAuditEvent[], nextBeforeId: (r.next_before_id ?? null) as number | null };
}

function parseOptions<Id>(raw: unknown, isKey: (v: unknown) => v is Id, labelKey: 'label' | 'name'): FacetOption<Id>[] | null {
  if (!Array.isArray(raw)) return null;
  const out: FacetOption<Id>[] = [];
  for (const o of raw) {
    if (!o || typeof o !== 'object') return null;
    const x = o as Record<string, unknown>;
    if (!isKey(x.id) || !isCount(x.count)) return null;
    out.push({ id: x.id, label: typeof x[labelKey] === 'string' && x[labelKey] ? (x[labelKey] as string) : 'Unnamed', count: x.count });
  }
  return out;
}

export function parseRoleAuditFacets(raw: unknown): RoleAuditFacets | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isCount(r.total) || !(r.oldest === null || isDate(r.oldest))) return null;
  const isUuid = (v: unknown): v is string => typeof v === 'string' && v.length > 0;
  const actors = parseOptions(r.actors, isUuid, 'label');
  const people = parseOptions(r.people, isUuid, 'label');
  const roles = parseOptions(r.roles, isId, 'name');
  if (!actors || !people || !roles || !r.types || typeof r.types !== 'object') return null;
  const types: Partial<Record<RoleAuditEventType, number>> = {};
  for (const [k, v] of Object.entries(r.types as Record<string, unknown>)) {
    if (ROLE_AUDIT_EVENT_TYPES.includes(k as RoleAuditEventType) && isCount(v)) types[k as RoleAuditEventType] = v;
  }
  return { total: r.total, oldest: r.oldest as string | null, actors, people, roles, types };
}

// ── Filters → RPC ─────────────────────────────────────────────────────────────

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Local midnight at the start of a `YYYY-MM-DD`, as ISO. */
export function startOfDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toISOString();
}

/** The range is inclusive of both days, so the end sent is the midnight AFTER `toDay`: [from, to). */
export function endOfDayExclusive(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d + 1).toISOString();
}

/** Why the range cannot be asked for, or null. Checked here so the admin sees it before a round trip. */
export function rangeProblem(f: Pick<RoleAuditFilters, 'fromDay' | 'toDay'>): string | null {
  if (f.fromDay && !DAY.test(f.fromDay)) return 'The start date is not a date.';
  if (f.toDay && !DAY.test(f.toDay)) return 'The end date is not a date.';
  if (f.fromDay && f.toDay && f.fromDay > f.toDay) return 'The start date is after the end date.';
  return null;
}

export function kindTypes(kind: RoleAuditKind | null): RoleAuditEventType[] | null {
  if (!kind) return null;
  const k = ROLE_AUDIT_KINDS.find((x) => x.id === kind);
  return k ? [...k.types] : null;
}

/** Every argument named, nulls included, so PostgREST never has to choose by which keys were sent. */
export function roleAuditArgs(f: RoleAuditFilters, beforeId: number | null, limit = PAGE_SIZE) {
  return {
    p_from: f.fromDay && DAY.test(f.fromDay) ? startOfDay(f.fromDay) : null,
    p_to: f.toDay && DAY.test(f.toDay) ? endOfDayExclusive(f.toDay) : null,
    p_actor_id: f.actorId,
    p_subject_user_id: f.affected?.kind === 'person' ? f.affected.id : null,
    p_role_id: f.affected?.kind === 'role' ? f.affected.id : null,
    p_event_types: kindTypes(f.kind),
    p_before_id: beforeId,
    p_limit: limit,
  };
}

export function isFiltered(f: RoleAuditFilters): boolean {
  return !!(f.fromDay || f.toDay || f.actorId || f.affected || f.kind);
}

/** "person:<uuid>" / "role:<id>": the value a single picker over both carries. */
export function affectedValue(a: AffectedFilter | null): string {
  return a ? `${a.kind}:${a.id}` : '';
}

export function parseAffectedValue(v: string): AffectedFilter | null {
  if (v.startsWith('person:') && v.length > 7) return { kind: 'person', id: v.slice(7) };
  if (v.startsWith('role:')) {
    const n = Number(v.slice(5));
    return Number.isInteger(n) && n > 0 ? { kind: 'role', id: n } : null;
  }
  return null;
}

// ── What a row says ───────────────────────────────────────────────────────────

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`);
const quoted = (name: string | null) => (name ? `"${name}"` : 'a role that no longer has a name');
const names = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const count = (v: unknown): number | null => (isCount(v) ? v : null);

/** Who did it. Never invents a name: nobody signed in is said as such. */
export function actorText(e: Pick<RoleAuditEvent, 'actorLabel' | 'joules'>): string {
  if (e.actorLabel) return e.actorLabel;
  return e.joules ? 'Joules' : 'Someone outside the app';
}

export function actorExplanation(e: Pick<RoleAuditEvent, 'actorLabel' | 'joules'>): string | null {
  if (e.actorLabel) return null;
  return 'Nobody was signed in: the database directly, a scheduled job, or Joules support.';
}

export interface RoleAuditLine {
  /** One sentence: who did what to whom. */
  summary: string;
  /** Optional second line: the consequence, or what the role held. */
  detail: string | null;
  /** True for the first-class administration events, which the screen marks. */
  administration: boolean;
  tone: 'gain' | 'loss' | 'neutral';
}

export function describeEvent(e: RoleAuditEvent): RoleAuditLine {
  const who = actorText(e);
  // Mid-sentence ("Changed by …"): a name keeps its capital, the fallback phrase does not.
  const by = e.actorLabel || e.joules ? who : who.charAt(0).toLowerCase() + who.slice(1);
  const role = quoted(e.roleName);
  const person = e.subjectLabel ?? 'Someone';
  const held = count(e.detail.holder_count);
  switch (e.type) {
    case 'role_created':
      return { summary: `${who} created the role ${role}.`, detail: null, administration: false, tone: 'neutral' };
    case 'role_renamed':
      return {
        summary: `${who} renamed ${quoted(typeof e.detail.from === 'string' ? e.detail.from : null)} to ${role}.`,
        detail: null,
        administration: false,
        tone: 'neutral',
      };
    case 'role_deleted': {
      const perms = names(e.detail.permissions);
      return {
        summary: `${who} deleted the role ${role}.`,
        detail:
          `It gave ${perms.length === 1 ? '1 permission' : `${perms.length} permissions`}` +
          (held === null ? '.' : held === 0 ? ' and nobody held it.' : ` and ${people(held)} held it.`),
        administration: false,
        tone: 'loss',
      };
    }
    case 'permissions_changed': {
      const added = names(e.detail.added).map(permissionTitle);
      const removed = names(e.detail.removed).map(permissionTitle);
      const parts = [added.length ? `gave it ${listOf(added)}` : null, removed.length ? `took away ${listOf(removed)}` : null].filter(Boolean);
      const restored = e.detail.source === 'restore';
      return {
        summary: `${who} ${restored ? 'restored an earlier version of' : 'changed what'} ${role}${restored ? '' : ' can do'}${parts.length ? `: ${parts.join(', and ')}` : ''}.`,
        detail: held === null ? null : held === 0 ? 'Nobody held this role at the time.' : `${people(held)} held this role at the time.`,
        administration: false,
        tone: removed.length && !added.length ? 'loss' : added.length && !removed.length ? 'gain' : 'neutral',
      };
    }
    case 'assignment_added':
      return { summary: `${who} gave ${person} the role ${role}.`, detail: viaLine(e), administration: false, tone: 'gain' };
    case 'assignment_removed':
      return { summary: `${who} took the role ${role} away from ${person}.`, detail: viaLine(e), administration: false, tone: 'loss' };
    case 'assignment_changed':
      return { summary: `${who} moved ${person} from ${quoted(e.fromRoleName)} to ${role}.`, detail: null, administration: false, tone: 'neutral' };
    case 'access_removed':
      return { summary: `${who} removed ${person}'s access.`, detail: 'They are signed out and can no longer sign in.', administration: false, tone: 'loss' };
    case 'access_restored':
      return { summary: `${who} restored ${person}'s access.`, detail: null, administration: false, tone: 'gain' };
    case 'administration_granted':
    case 'administration_removed': {
      const gained = e.type === 'administration_granted';
      if (!e.subjectId && e.detail.via === 'role_deleted') {
        return {
          summary: `${role}, which could administer roles and permissions, was deleted.`,
          detail: `Changed by ${by}.`,
          administration: true,
          tone: 'loss',
        };
      }
      if (!e.subjectId) {
        return {
          summary: `${role} ${gained ? 'can now' : 'can no longer'} administer roles and permissions.`,
          detail: held === null ? `Changed by ${by}.` : `${held === 0 ? 'Nobody held it' : `${people(held)} held it`} at the time. Changed by ${by}.`,
          administration: true,
          tone: gained ? 'gain' : 'loss',
        };
      }
      const how = e.detail.via === 'access' ? (gained ? 'Their access was restored.' : 'Their access was removed.') : `Through the role ${role}.`;
      return {
        summary: `${person} ${gained ? 'can now' : 'can no longer'} administer roles and permissions.`,
        detail: `${how} Changed by ${by}.`,
        administration: true,
        tone: gained ? 'gain' : 'loss',
      };
    }
  }
}

function viaLine(e: RoleAuditEvent): string | null {
  if (typeof e.detail.via_parent_account === 'string') return 'Given automatically by the parent account.';
  if (e.detail.moved_to_another_account === true) return 'They moved to another account.';
  if (e.detail.moved_from_another_account === true) return 'They moved here from another account.';
  return null;
}

/** "3 changes", "1 change": the count under the filters. */
export function changesCount(n: number): string {
  return n === 1 ? '1 change' : `${n} changes`;
}

/** What the empty state says, which depends on whether anything is filtered. */
export function emptyText(f: RoleAuditFilters, facets: Pick<RoleAuditFacets, 'total'> | null): string {
  if (isFiltered(f)) return 'No changes match these filters.';
  if (facets && facets.total === 0) return 'Nothing has changed yet. Role and access changes appear here from now on.';
  return 'No changes to show.';
}
